<template>
  <div class="color-variant-picker" :class="{ '-small': small && !thumbs }">
    <label v-if="label" class="h5 -ellipsis">
      <span class="-bold">{{ insertString("Global", "Color") }}</span>
      &nbsp;<span role="status" class="-thin"
        >{{ colorName }} <span v-if="statusText">({{ statusText }})</span></span
      >
    </label>
    <div
      class="color-variant-picker-colors"
      role="radiogroup"
      :class="!thumbs ? '-dots' : '-thumbs'"
    >
      <button
        @click="changeColor(index)"
        @keypress.enter="changeColor(index)"
        :title="`${item.ColorName}, ${item.BaseColor}`"
        v-for="(item, index) in variants"
        :key="index"
        @mouseenter="!small ? (hoverIndex = index) : (hoverIndex = -1)"
        @mouseleave="hoverIndex = -1"
        :aria-checked="modelValue === index ? 'true' : 'false'"
        role="radio"
        class="-stripped"
        :class="{
          '-loaded': loadedImages[index],
          '-selected': modelValue === index || hoverIndex === index,
          '-black': black,
        }"
      >
        <SvgComponent
          v-if="!thumbs"
          :icon="{
            name: item.SecondaryColorHex ? 'DotMultiColor' : 'DotTransform',
            color: item.ColorHex,
            secondaryColor: item.SecondaryColorHex
              ? item.SecondaryColorHex
              : null,
            size: !small ? 16 : 10,
          }"
          :active="modelValue === index || hoverIndex === index"
        />
        <img v-else :src="imageUrl(item)" :alt="`${item.ColorName}, ${item.BaseColor}`" @load="loadedImg(index)" />
      </button>
    </div>
  </div>
</template>
<script>

// import { eventBus } from '@/Scripts/lib/helper/eventBus.js';
import GenderHelper from '@/Scripts/lib/helper/genderHelper.js';
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';
import GenerateImageHelper from '@/Scripts/lib/helper/generateImageHelper.js';

export default {
  name: 'ColorVariantPicker',
  components: {
    SvgComponent
  },
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    /**
     * List of variant-objects
     */
    variants: Array,
    /**
     *  variant index to select on load
     */
    modelValue: {
      type: Number,
      required: true
    },
    /**
     * View label over colorchanger
     */
    label: {
      type: Boolean,
      default: false
    },
    gender: String,
    /**
     * Small version (new productcards mobile)
     */
    small: Boolean,
    thumbs: Boolean,
    largeThumbs: Boolean,
    discoObject: Object,
    black: Boolean
  },
  data () {
    return {
      loadedImages: [],
      hoverIndex: -1,
      discoIndex: 0

    };
  },
  computed: {
    statusText () {
      if (this.modelValue === -1 || !this.variants[this.modelValue]) {
        return '';
      }
      return this.variants[this.modelValue].StatusText;
    },
    colorName () {
      if (this.modelValue === -1 || !this.variants[this.modelValue]) {
        return '';
      }
      return this.variants[this.modelValue].ColorName;
    }
  },
  methods: {
    imageAlt (variant) {
      let image;
      if (GenderHelper.getGender() === 'women') {
        image = this.findWomanForVariant(variant);
      } else {
        image = this.findImageForVariant(variant);
      }
      const titleFromType = image.Type ? image.Type.match(/[A-Z][a-z]+/g).join(' ') : '';
      return image.AltText || image.Description || titleFromType;
    },
    imageUrl (variant) {
      let image;
      if (GenderHelper.getGender() === 'women') {
        image = this.findWomanForVariant(variant);
      } else {
        image = this.findImageForVariant(variant);
      }
      const border = image.Type.includes('Model') ? 0 : this.largeThumbs ? 9 : 36;
      return GenerateImageHelper.generateFocal(image.Path, { width: 180, height: 225, bgcolor: '#F5F5F5', border: border });
    },
    findWomanForVariant (variant) {
      const filtered = variant.MoreImages.filter(i => i.Type.includes('Women'));
      if (filtered.length) { return filtered[0]; }
      return this.findImageForVariant(variant);
    },
    findImageForVariant (variant) {
      if (variant.ModelShotPath) { return { Path: variant.ModelShotPath, Type: 'ModelShot' }; }
      if (variant.MoreImages.length) { return variant.MoreImages[0]; }
      if (variant.PackShotPath) { return { Path: variant.PackShotPath, Type: 'PackShot' }; }
      return { Path: '', Type: 'NotFound' };
    },
    loadedImg (index) {
      this.$set(this.loadedImages, index, true);
    },
    changeColor (index) {
      this.$emit('change', index);
    }
  }

};
</script>
<style lang="scss">
@import "@/scss/defaults.scss";

.color-variant-picker {
  display: flex;
  flex-direction: column;

  .h5 {
    margin-bottom: 12px;
    @include font-responsive(small, regular, small-caps);

    span {
      @include font-responsive(small, thin, small-caps);
    }
  }
  &-colors {
    &.-dots {
      display: flex;

      > button {
        padding: 0 9px;
        cursor: pointer;
        &:first-of-type {
          padding-left: 0;
        }
      }
    }
    &.-thumbs {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-gap: 9px;
      @include media-breakpoint-up(lg) {
        grid-gap: 18px;
      }

      > button {
        background-color: $gray-lighter;

        img {
          width: 100%;
          padding-bottom: 125%;
          opacity: 0;
          height: 0;
          transition: opacity 0.33s;
          border: 1px solid transparent;
        }
        &.-loaded {
          img {
            height: auto;
            opacity: 1;
            padding-bottom: 0;
          }
        }
        &.-selected {
          img {
            border-color: $gray-dark;
          }
        }
        .img-loader {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
        }
        &.-black {
          background-color: #000000;
          &.-selected {
            img {
              border-color: $black-light;
            }
          }
        }
      }
    }
  }
  &.-small .color-variant-picker-colors {
    &.-dots {
      flex-wrap: wrap;
      > button {
        padding: 0 4px;
        &:first-of-type {
          // padding-left: 0;
        }
      }
    }
  }
}
</style>
